import React, { useState } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Stack,
} from "@mui/material";
import DrawerNav from "../Navigation/DrawerNav";
import { BASE_URL } from "../../Api/constants";

const MediaUploadMultiple = () => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadedUrls, setUploadedUrls] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleFileChange = (event) => {
    const files = event.target.files;
    setSelectedFiles(files);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      alert("Please select files to upload.");
      return;
    }

    setLoading(true);

    const formData = new FormData();
    for (let i = 0; i < selectedFiles.length; i++) {
      formData.append("files", selectedFiles[i]);
    }

    try {
      const response = await axios.post(
        `${BASE_URL}/api/v2/s3/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      // Assuming the response contains an array of objects with imageId and url
      const uploadedFiles = response.data.data.uploadedFiles.map(
        (file) => file.url
      );
      setUploadedUrls(uploadedFiles);
      setLoading(false);
    } catch (error) {
      console.error("Error uploading the files", error.response);
      setLoading(false);
    }
  };

  const handleCopyUrl = (url) => {
    navigator.clipboard
      .writeText(url)
      .then(() => {
        alert("URL copied to clipboard");
      })
      .catch((err) => {
        console.error("Failed to copy the URL:", err);
      });
  };

  const renderFilePreview = (url) => {
    const fileType = url.split(".").pop().toLowerCase();

    if (
      fileType === "jpg" ||
      fileType === "jpeg" ||
      fileType === "png" ||
      fileType === "gif"
    ) {
      return (
        <img
          src={url}
          alt="Uploaded"
          style={{ maxWidth: "100%", height: "auto" }}
        />
      );
    } else if (fileType === "mp4" || fileType === "mov") {
      return (
        <video controls style={{ maxWidth: "100%", height: "auto" }}>
          <source src={url} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      );
    } else if (fileType === "mp3" || fileType === "wav") {
      return (
        <audio controls style={{ width: "100%" }}>
          <source src={url} type="audio/mpeg" />
        </audio>
      );
    } else if (fileType === "pdf") {
      return (
        <iframe
          src={url}
          width="100%"
          height="500px"
          title="PDF Preview"
          style={{ maxWidth: "100%", height: "auto" }}
        />
      );
    }

    return null;
  };

  return (
    <Box sx={{ display: "flex" }}>
      <DrawerNav />
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Box p={2}>
          <div className="w-full my-10 p-6 bg-white shadow-lg rounded-lg">
            <Typography
              variant="h4"
              component="div"
              sx={{ fontWeight: "bold", mb: 2 }}
            >
              Upload Multiple Media Files
            </Typography>

            {!loading && !uploadedUrls.length && (
              <>
                <input
                  type="file"
                  onChange={handleFileChange}
                  accept="image/*,video/*,audio/*,application/pdf"
                  multiple
                  className="block w-full text-sm text-gray-500
                                file:mr-4 file:py-2 file:px-4
                                file:rounded-full file:border-0
                                file:text-sm file:font-semibold
                                file:bg-indigo-50 file:text-indigo-700
                                hover:file:bg-indigo-100
                                mb-4"
                />
                <button
                  onClick={handleUpload}
                  className="bg-indigo-600 text-white py-2 px-4 rounded hover:bg-indigo-700 transition duration-300"
                >
                  Upload Files
                </button>
              </>
            )}

            {loading && (
              <div className="flex justify-center my-10">
                <CircularProgress />
              </div>
            )}

            {uploadedUrls.length > 0 && !loading && (
              <div className="mt-4">
                <button
                  onClick={() => window.location.reload()}
                  className="bg-indigo-600 text-white py-2 px-4 my-4 rounded hover:bg-indigo-700 transition duration-300"
                >
                  Upload More Files
                </button>

                <Typography variant="h6" sx={{ mb: 2 }}>
                  Uploaded Files:
                </Typography>
                <Stack spacing={2}>
                  {uploadedUrls.map((url, index) => (
                    <Box key={index}>
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-600 underline"
                      >
                        {url}
                      </a>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => handleCopyUrl(url)}
                        sx={{ mt: 1 }}
                      >
                        Copy URL
                      </Button>
                      <div>{renderFilePreview(url)}</div>
                    </Box>
                  ))}
                </Stack>
              </div>
            )}
          </div>
        </Box>
      </Box>
    </Box>
  );
};

export default MediaUploadMultiple;
